// @ts-nocheck
// Enterprise Deployment Validation — Fase 14.8
import { validateConfig, enterpriseConfig } from '@/core/config/enterpriseConfigRegistry'
import { getFlags }                         from '@/core/featureFlags'

export type ValidationSeverity = 'pass' | 'warn' | 'fail'
export interface ValidationCheck { id: string; category: 'env' | 'config' | 'runtime' | 'build' | 'compat'; name: string; severity: ValidationSeverity; message: string; detail?: string }
export interface EnterpriseDeploymentReport { timestamp: string; environment: 'development' | 'staging' | 'production'; deployReady: boolean; checks: ValidationCheck[]; summary: { pass: number; warn: number; fail: number }; blockers: ValidationCheck[] }

function checkEnvVars(): ValidationCheck[] {
  const checks: ValidationCheck[] = []
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined
  checks.push({ id: 'env.supabase_url', category: 'env', name: 'Supabase URL', severity: url?.startsWith('https://') ? 'pass' : 'fail', message: url?.startsWith('https://') ? 'VITE_SUPABASE_URL set' : 'VITE_SUPABASE_URL missing or invalid' })
  const key = (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? import.meta.env.VITE_SUPABASE_ANON_KEY) as string | undefined
  checks.push({ id: 'env.supabase_key', category: 'env', name: 'Supabase Key', severity: key && key.length > 20 ? 'pass' : 'fail', message: key ? 'Supabase key set' : 'Supabase key missing' })
  const version = import.meta.env.VITE_APP_VERSION as string | undefined
  checks.push({ id: 'env.app_version', category: 'env', name: 'App Version', severity: version ? 'pass' : 'warn', message: version ? `Version: ${version}` : 'VITE_APP_VERSION not set' })
  return checks
}

function checkEnterpriseConfig(companyId?: string): ValidationCheck[] {
  const checks: ValidationCheck[] = []
  const cfg = enterpriseConfig.getConfig(companyId)
  const v   = validateConfig(cfg)
  if (v.valid) checks.push({ id: 'config.valid', category: 'config', name: 'Enterprise Config', severity: 'pass', message: 'Config validated' })
  v.errors.forEach((e) => checks.push({ id: `config.err.${e.slice(0,20)}`, category: 'config', name: 'Config Error', severity: 'fail', message: e }))
  v.warnings.forEach((w) => checks.push({ id: `config.warn.${w.slice(0,20)}`, category: 'config', name: 'Config Warning', severity: 'warn', message: w }))
  return checks
}

function checkBrowserCompatibility(): ValidationCheck[] {
  const checks: ValidationCheck[] = []
  checks.push({ id: 'compat.websocket', category: 'compat', name: 'WebSocket', severity: typeof WebSocket !== 'undefined' ? 'pass' : 'fail', message: typeof WebSocket !== 'undefined' ? 'WebSocket available' : 'WebSocket not available' })
  const hasMemory = typeof performance !== 'undefined' && 'memory' in performance
  checks.push({ id: 'compat.perf_memory', category: 'compat', name: 'Heap monitor', severity: hasMemory ? 'pass' : 'warn', message: hasMemory ? 'Heap monitoring available' : 'performance.memory unavailable (non-Chromium)' })
  let storageOk = false
  try { localStorage.setItem('__canteiro_probe__', '1'); localStorage.removeItem('__canteiro_probe__'); storageOk = true } catch { /* ignore */ }
  checks.push({ id: 'compat.localstorage', category: 'compat', name: 'localStorage', severity: storageOk ? 'pass' : 'warn', message: storageOk ? 'localStorage available' : 'localStorage unavailable' })
  return checks
}

function checkFeatureFlags(): ValidationCheck[] {
  const checks: ValidationCheck[] = []
  const flags = getFlags()
  if (import.meta.env.PROD) {
    const betaFlags = ['ENABLE_DIGITAL_TWIN', 'ENABLE_CAMERA_WALL', 'ENABLE_PPE_DETECTION', 'ENABLE_VISION_AI'] as const
    const betaEnabled = betaFlags.filter((f) => flags[f])
    if (betaEnabled.length > 0) checks.push({ id: 'flags.beta_in_prod', category: 'config', name: 'Beta Flags in Prod', severity: 'warn', message: `${betaEnabled.length} beta flag(s) enabled: ${betaEnabled.join(', ')}` })
  }
  checks.push({ id: 'flags.loaded', category: 'config', name: 'Feature Flags', severity: 'pass', message: `${Object.keys(flags).length} flags loaded` })
  return checks
}

export function validateEnterpriseDeployment(companyId?: string): EnterpriseDeploymentReport {
  const allChecks = [...checkEnvVars(), ...checkEnterpriseConfig(companyId), ...checkBrowserCompatibility(), ...checkFeatureFlags()]
  const pass = allChecks.filter((c) => c.severity === 'pass').length
  const warn = allChecks.filter((c) => c.severity === 'warn').length
  const fail = allChecks.filter((c) => c.severity === 'fail').length
  const blockers = allChecks.filter((c) => c.severity === 'fail')
  const environment = import.meta.env.PROD ? 'production' : import.meta.env.VITE_ENV === 'staging' ? 'staging' : 'development'
  return { timestamp: new Date().toISOString(), environment, deployReady: fail === 0, checks: allChecks, summary: { pass, warn, fail }, blockers }
}

export function printDeploymentReport(report: EnterpriseDeploymentReport): void {
  const icon = report.deployReady ? 'OK' : 'FAIL'
  console.groupCollapsed(`[Canteiro] Deploy ${icon} — ${report.environment.toUpperCase()} | pass:${report.summary.pass} warn:${report.summary.warn} fail:${report.summary.fail}`)
  report.blockers.forEach((c) => console.error(`[${c.category}] ${c.name}: ${c.message}`))
  report.checks.filter((c) => c.severity === 'warn').forEach((c) => console.warn(`[${c.category}] ${c.name}: ${c.message}`))
  console.groupEnd()
}
